import React from 'react';

import { useTranslation } from 'react-i18next';

import { ChevronIcon, CloseIcon, SortIcon } from '@icons';
import { DiamondIcon, FlagFilledIcon, GiftIcon } from '@icons';

import {
	BottomSheetModal,
	Section,
	SortOptions,
	SortItem,
	FilterChips,
	Buttons
} from '@shared/components/bottom-sheet-modal';

import { COIN_RACE_ORDER, COIN_RACE_TYPE, CoinRaceFilter, CoinRaceSortOption } from '@types';

import { Button } from '@components/button';

import * as Styled from './race-filters.styled';

type OwnProps = {
	selectedType?: COIN_RACE_TYPE;
	sortOrder: COIN_RACE_ORDER;
	onTypeChange: (type?: COIN_RACE_TYPE) => void;
	onSortChange: (order: COIN_RACE_ORDER) => void;
};

const RaceFilters: React.FC<OwnProps> = ({ selectedType, sortOrder, onTypeChange, onSortChange }) => {
	const { t } = useTranslation('coin_race');
	const [isSortDropdownOpen, setIsSortDropdownOpen] = React.useState(false);
	const [isMobileModalOpen, setIsMobileModalOpen] = React.useState(false);

	const filters: CoinRaceFilter[] = React.useMemo(
		() => [
			{ id: COIN_RACE_TYPE.FIXED_PRIZE, name: 'bonuses', icon: <GiftIcon /> },
			{ id: COIN_RACE_TYPE.JACKPOT_PRIZE, name: 'jackpot', icon: <DiamondIcon /> },
			{ id: COIN_RACE_TYPE.MY_RACES, name: 'filters.my_races', icon: <FlagFilledIcon /> }
		],
		[]
	);

	const sortOptions: CoinRaceSortOption[] = React.useMemo(
		() => [
			{ label: 'to_increase', value: COIN_RACE_ORDER.ASC },
			{ label: 'to_decrease', value: COIN_RACE_ORDER.DESC }
		],
		[]
	);

	const handleFilterToggle = (filterId: COIN_RACE_TYPE) => {
		const newSelectedType = selectedType === filterId ? undefined : filterId;
		onTypeChange(newSelectedType);
	};

	const handleSortChangeInternal = (order: COIN_RACE_ORDER) => {
		onSortChange(order);
		setIsSortDropdownOpen(false);
	};

	const handleSortDropdownClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setIsSortDropdownOpen(!isSortDropdownOpen);
	};

	const handleResetFilters = () => {
		onTypeChange(undefined);
		onSortChange(COIN_RACE_ORDER.ASC);
	};

	const handleApplyMobileFilters = () => {
		setIsMobileModalOpen(false);
	};

	React.useEffect(() => {
		const handleClickOutside = () => {
			setIsSortDropdownOpen(false);
		};

		document.addEventListener('click', handleClickOutside);
		return () => document.removeEventListener('click', handleClickOutside);
	}, []);

	const hasActiveFilters = selectedType !== undefined;

	const currentSortOption = React.useMemo(
		() => sortOptions.find(option => option.value === sortOrder),
		[sortOptions, sortOrder]
	);

	return (
		<>
			<Styled.FiltersContainer>
				<Styled.ChipsSection>
					{filters.map(filter => (
						<Styled.FilterChip
							key={filter.id}
							$selected={selectedType === filter.id}
							onClick={() => handleFilterToggle(filter.id)}
						>
							{filter.icon}
							{t(filter.name)}
							<Styled.ChipCloseIcon $visible={selectedType === filter.id}>
								<CloseIcon.Rounded />
							</Styled.ChipCloseIcon>
						</Styled.FilterChip>
					))}

					{hasActiveFilters && (
						<Styled.ResetFilterContainer>
							<Styled.ChipsDivider>|</Styled.ChipsDivider>
							<Styled.ResetButton onClick={handleResetFilters}>
								{t('reset_filters')}
								<CloseIcon.Rounded />
							</Styled.ResetButton>
						</Styled.ResetFilterContainer>
					)}
				</Styled.ChipsSection>

				<Styled.RightActionsContainer>
					<Styled.SortDropdown onClick={handleSortDropdownClick} data-sort-dropdown>
						{t(currentSortOption?.label || 'product_filter')}
						<ChevronIcon.Down />

						<Styled.DropdownContent $isOpen={isSortDropdownOpen}>
							{sortOptions.map(option => (
								<Styled.DropdownItem
									key={option.value}
									$selected={sortOrder === option.value}
									onClick={() => handleSortChangeInternal(option.value)}
								>
									{t(option.label)}
								</Styled.DropdownItem>
							))}
						</Styled.DropdownContent>
					</Styled.SortDropdown>

					<Styled.MobileSortIcon onClick={() => setIsMobileModalOpen(true)}>
						<SortIcon />
					</Styled.MobileSortIcon>
				</Styled.RightActionsContainer>
			</Styled.FiltersContainer>

			<BottomSheetModal
				isOpen={isMobileModalOpen}
				onClose={() => setIsMobileModalOpen(false)}
				title={
					<Styled.MobileFilterTitle>
						<SortIcon /> {t('filter')}
					</Styled.MobileFilterTitle>
				}
			>
				<Section>
					{t('filters.title')}
					<SortOptions>
						{sortOptions.map(option => (
							<SortItem
								key={option.value}
								$selected={sortOrder === option.value}
								onClick={() => handleSortChangeInternal(option.value)}
							>
								{t(option.label)}
							</SortItem>
						))}
					</SortOptions>
				</Section>

				<Section>
					{t('filters.races')}:
					<FilterChips>
						{filters.map(filter => (
							<Styled.FilterChip
								key={filter.id}
								$selected={selectedType === filter.id}
								onClick={() => handleFilterToggle(filter.id)}
							>
								{filter.icon}
								{t(filter.name)}
							</Styled.FilterChip>
						))}
					</FilterChips>
				</Section>

				<Buttons>
					<Button fluid color='primary' onClick={handleApplyMobileFilters}>
						{t('apply')}
					</Button>

					<Button fluid color='dark' onClick={handleResetFilters}>
						{t('reset_all')}
					</Button>
				</Buttons>
			</BottomSheetModal>
		</>
	);
};

export default React.memo(RaceFilters);
